import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Plus, Edit, Trash2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      localStorage.setItem('admin_token', response.data.access_token);
      toast.success('Login successful!');
      onLogin(response.data.access_token);
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-xl">
        <h2 data-testid="admin-login-title" className="font-heading text-3xl font-bold mb-6 text-center">
          Admin Login
        </h2>
        <form onSubmit={handleSubmit} data-testid="admin-login-form" className="space-y-4">
          <div>
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              data-testid="admin-username"
              value={credentials.username}
              onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
              required
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              data-testid="admin-password"
              value={credentials.password}
              onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              required
              className="mt-2"
            />
          </div>
          <Button
            type="submit"
            data-testid="admin-login-submit"
            disabled={isLoading}
            className="w-full bg-primary text-white hover:bg-primary-600 py-6 rounded-full"
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </Button>
        </form>
        <p className="mt-4 text-sm text-slate-500 text-center">
          Default: username: admin, password: obiquiste2024
        </p>
      </div>
    </div>
  );
};

const BlogManager = ({ token }) => {
  const [blogs, setBlogs] = useState([]);
  const [editingBlog, setEditingBlog] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    image_url: '',
    meta_title: '',
    meta_description: '',
    published: true,
  });

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs?published_only=false`);
      setBlogs(response.data);
    } catch (error) {
      toast.error('Failed to fetch blogs');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBlog) {
        await axios.put(`${API}/blogs/${editingBlog.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Blog updated successfully');
      } else {
        await axios.post(`${API}/blogs`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Blog created successfully');
      }
      fetchBlogs();
      resetForm();
      setIsDialogOpen(false);
    } catch (error) {
      toast.error('Failed to save blog');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;
    try {
      await axios.delete(`${API}/blogs/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Blog deleted successfully');
      fetchBlogs();
    } catch (error) {
      toast.error('Failed to delete blog');
    }
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title,
      slug: blog.slug,
      excerpt: blog.excerpt,
      content: blog.content,
      image_url: blog.image_url || '',
      meta_title: blog.meta_title || '',
      meta_description: blog.meta_description || '',
      published: blog.published,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingBlog(null);
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      image_url: '',
      meta_title: '',
      meta_description: '',
      published: true,
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Blog Management</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="create-blog-button"
              onClick={resetForm}
              className="bg-primary text-white hover:bg-primary-600"
            >
              <Plus className="mr-2" size={18} />
              Create Blog
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto z-[100]">
            <DialogHeader>
              <DialogTitle>{editingBlog ? 'Edit Blog' : 'Create New Blog'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  data-testid="blog-form-title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Slug (URL-friendly) *</Label>
                <Input
                  data-testid="blog-form-slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Excerpt *</Label>
                <Textarea
                  data-testid="blog-form-excerpt"
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  required
                  rows={3}
                />
              </div>
              <div>
                <Label>Content (HTML) *</Label>
                <Textarea
                  data-testid="blog-form-content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                  rows={8}
                />
              </div>
              <div>
                <Label>Image URL</Label>
                <Input
                  data-testid="blog-form-image"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                />
              </div>
              <div>
                <Label>Meta Title (SEO)</Label>
                <Input
                  value={formData.meta_title}
                  onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                />
              </div>
              <div>
                <Label>Meta Description (SEO)</Label>
                <Textarea
                  value={formData.meta_description}
                  onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="published"
                  checked={formData.published}
                  onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="published">Published</Label>
              </div>
              <Button
                type="submit"
                data-testid="blog-form-submit"
                className="w-full bg-primary text-white hover:bg-primary-600"
              >
                {editingBlog ? 'Update Blog' : 'Create Blog'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {blogs.map((blog) => (
              <TableRow key={blog.id} data-testid={`blog-row-${blog.id}`}>
                <TableCell className="font-medium">{blog.title}</TableCell>
                <TableCell>{blog.slug}</TableCell>
                <TableCell>
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${
                      blog.published
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {blog.published ? 'Published' : 'Draft'}
                  </span>
                </TableCell>
                <TableCell>
                  {new Date(blog.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(blog)}
                      data-testid={`edit-blog-${blog.id}`}
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(blog.id)}
                      data-testid={`delete-blog-${blog.id}`}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const CaseStudyManager = ({ token }) => {
  const [studies, setStudies] = useState([]);
  const [editingStudy, setEditingStudy] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    client_name: '',
    marketplace: '',
    challenge: '',
    solution: '',
    results: {},
    image_url: '',
    testimonial: '',
    published: true,
  });
  const [resultsInput, setResultsInput] = useState('');

  useEffect(() => {
    fetchCaseStudies();
  }, []);

  const fetchCaseStudies = async () => {
    try {
      const response = await axios.get(`${API}/case-studies?published_only=false`);
      setStudies(response.data);
    } catch (error) {
      toast.error('Failed to fetch case studies');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const resultsObj = {};
      resultsInput.split('\n').forEach((line) => {
        const [key, value] = line.split(':').map((s) => s.trim());
        if (key && value) resultsObj[key] = value;
      });

      const submitData = { ...formData, results: resultsObj };

      if (editingStudy) {
        await axios.put(`${API}/case-studies/${editingStudy.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Case study updated successfully');
      } else {
        await axios.post(`${API}/case-studies`, submitData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Case study created successfully');
      }
      fetchCaseStudies();
      resetForm();
      setIsDialogOpen(false);
    } catch (error) {
      toast.error('Failed to save case study');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this case study?')) return;
    try {
      await axios.delete(`${API}/case-studies/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Case study deleted successfully');
      fetchCaseStudies();
    } catch (error) {
      toast.error('Failed to delete case study');
    }
  };

  const handleEdit = (study) => {
    setEditingStudy(study);
    setFormData({
      title: study.title,
      slug: study.slug,
      client_name: study.client_name,
      marketplace: study.marketplace,
      challenge: study.challenge,
      solution: study.solution,
      results: study.results,
      image_url: study.image_url || '',
      testimonial: study.testimonial || '',
      published: study.published,
    });
    const resultsStr = Object.entries(study.results)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n');
    setResultsInput(resultsStr);
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingStudy(null);
    setFormData({
      title: '',
      slug: '',
      client_name: '',
      marketplace: '',
      challenge: '',
      solution: '',
      results: {},
      image_url: '',
      testimonial: '',
      published: true,
    });
    setResultsInput('');
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Case Study Management</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="create-case-study-button"
              onClick={resetForm}
              className="bg-primary text-white hover:bg-primary-600"
            >
              <Plus className="mr-2" size={18} />
              Create Case Study
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto z-[100]">
            <DialogHeader>
              <DialogTitle>
                {editingStudy ? 'Edit Case Study' : 'Create New Case Study'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  data-testid="case-study-form-title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Slug (URL-friendly) *</Label>
                <Input
                  data-testid="case-study-form-slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Client Name *</Label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Marketplace *</Label>
                  <Input
                    value={formData.marketplace}
                    onChange={(e) => setFormData({ ...formData, marketplace: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Challenge *</Label>
                <Textarea
                  value={formData.challenge}
                  onChange={(e) => setFormData({ ...formData, challenge: e.target.value })}
                  required
                  rows={4}
                />
              </div>
              <div>
                <Label>Solution *</Label>
                <Textarea
                  value={formData.solution}
                  onChange={(e) => setFormData({ ...formData, solution: e.target.value })}
                  required
                  rows={4}
                />
              </div>
              <div>
                <Label>Results (Format: Key: Value, one per line) *</Label>
                <Textarea
                  value={resultsInput}
                  onChange={(e) => setResultsInput(e.target.value)}
                  placeholder="Revenue Growth: 200%\nACoS Reduction: 35%"
                  required
                  rows={4}
                />
              </div>
              <div>
                <Label>Image URL</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                />
              </div>
              <div>
                <Label>Client Testimonial</Label>
                <Textarea
                  value={formData.testimonial}
                  onChange={(e) => setFormData({ ...formData, testimonial: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="case-published"
                  checked={formData.published}
                  onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="case-published">Published</Label>
              </div>
              <Button
                type="submit"
                data-testid="case-study-form-submit"
                className="w-full bg-primary text-white hover:bg-primary-600"
              >
                {editingStudy ? 'Update Case Study' : 'Create Case Study'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Marketplace</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {studies.map((study) => (
              <TableRow key={study.id} data-testid={`case-study-row-${study.id}`}>
                <TableCell className="font-medium">{study.title}</TableCell>
                <TableCell>{study.client_name}</TableCell>
                <TableCell>{study.marketplace}</TableCell>
                <TableCell>
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${
                      study.published
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {study.published ? 'Published' : 'Draft'}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(study)}
                      data-testid={`edit-case-study-${study.id}`}
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(study.id)}
                      data-testid={`delete-case-study-${study.id}`}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setToken(savedToken);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    toast.success('Logged out successfully');
  };

  if (!token) {
    return <AdminLogin onLogin={setToken} />;
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-16">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex justify-between items-center mb-8">
          <h1 data-testid="admin-dashboard-title" className="text-4xl font-bold">Admin Dashboard</h1>
          <Button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50"
          >
            <LogOut className="mr-2" size={18} />
            Logout
          </Button>
        </div>

        <Tabs defaultValue="blogs" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="blogs" data-testid="tab-blogs">
              Blog Posts
            </TabsTrigger>
            <TabsTrigger value="case-studies" data-testid="tab-case-studies">
              Case Studies
            </TabsTrigger>
          </TabsList>
          <TabsContent value="blogs">
            <BlogManager token={token} />
          </TabsContent>
          <TabsContent value="case-studies">
            <CaseStudyManager token={token} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
